import { BaseApiService } from './base';
import { buildApiUrl } from '../../utils/apiUrl';

// Types pour la génération de contenu
export interface PromptAnalysis {
  session_id: number;
  original_prompt: string;
  suggested_prompt: string;
  analysis: string;
  document_type: string;
  document_subtype: string;
  status: string;
  model_used?: string;
  skip_improvement?: boolean;
  alternative_suggestions?: string[];
  model_available?: boolean;
  message?: string;
  error?: string;
}

export interface GeneratedContent {
  session_id: number;
  content: string;
  metadata: {
    document_type: string;
    document_subtype: string;
    model_used: string;
    prompt_length: number;
    content_length: number;
    model_available?: boolean;
    message?: string;
  };
  status: string;
  final_prompt: string;
  error?: string;
}

export interface ContentGenerationSession {
  session_id: number;
  document_type: string;
  document_subtype: string;
  original_prompt: string;
  suggested_prompt: string;
  final_prompt: string;
  generated_content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SessionListResponse {
  sessions: Array<{
    session_id: number;
    document_type: string;
    document_subtype: string;
    original_prompt: string;
    status: string;
    created_at: string;
    has_content: boolean;
  }>;
  count: number;
}

export class ContentGenerationService extends BaseApiService {
  private baseEndpoint = '/documents/content-generation';

  /**
   * Analyse un prompt utilisateur et suggère une version améliorée
   */
  async analyzePrompt(
    prompt: string,
    documentType: string,
    documentSubtype: string = '',
    documentSubtypeId?: number
  ): Promise<PromptAnalysis> {
    try {
      console.log('🔍 Analyse du prompt:', { prompt, documentType, documentSubtype, documentSubtypeId });
      
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(`${this.baseEndpoint}/analyze_prompt/`), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          prompt,
          document_type: documentType,
          document_subtype: documentSubtype,
          document_subtype_id: documentSubtypeId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Analyse réussie:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse du prompt:', error);
      throw error;
    }
  }

  /**
   * Génère le contenu du document basé sur le prompt
   */
  async generateContent(
    sessionId: number,
    prompt: string,
    documentType: string,
    documentSubtype: string = '',
    acceptSuggestion: boolean = false,
    documentSubtypeId?: number
  ): Promise<GeneratedContent> {
    try {
      console.log('📄 Génération de contenu:', { 
        sessionId, 
        prompt, 
        documentType, 
        documentSubtype, 
        acceptSuggestion,
        documentSubtypeId
      });
      
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(`${this.baseEndpoint}/generate_content/`), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          prompt,
          document_type: documentType,
          document_subtype: documentSubtype,
          accept_suggestion: acceptSuggestion,
          document_subtype_id: documentSubtypeId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Génération réussie:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la génération de contenu:', error);
      throw error;
    }
  }

  /**
   * Récupère une session de génération
   */
  async getSession(sessionId: number): Promise<ContentGenerationSession> {
    try {
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(
        buildApiUrl(`${this.baseEndpoint}/get_session/?session_id=${sessionId}`),
        {
          method: 'GET',
          headers: this.getHeaders()
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la session:', error);
      throw error;
    }
  }

  /**
   * Liste les sessions de génération de l'utilisateur
   */
  async listSessions(): Promise<SessionListResponse> {
    try {
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(`${this.baseEndpoint}/list_sessions/`), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des sessions:', error);
      throw error;
    }
  }

  /**
   * Génère et télécharge un document Word
   */
  async downloadDocument(sessionId: number, content: string, documentType: string, documentSubtype: string): Promise<Blob> {
    try {
      console.log('📄 Génération du fichier Word:', { sessionId, documentType, documentSubtype });
      console.log('📄 Contenu (premiers 200 caractères):', content.substring(0, 200) + '...');
      
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(`${this.baseEndpoint}/download/`), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          content,
          document_type: documentType,
          document_subtype: documentSubtype
        })
      });

      if (!response.ok) {
        // Essayer de parser l'erreur JSON
        let errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        try {
          // Cloner la réponse pour pouvoir la lire plusieurs fois si nécessaire
          const responseClone = response.clone();
          const errorData = await responseClone.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Si ce n'est pas du JSON, essayer de lire le texte
          try {
            const responseClone = response.clone();
            const errorText = await responseClone.text();
            errorMessage = errorText || errorMessage;
          } catch (e2) {
            console.error('Impossible de lire le message d\'erreur:', e2);
          }
        }
        console.error('❌ Erreur du serveur:', errorMessage);
        throw new Error(errorMessage);
      }

      // Vérifier que la réponse est bien un blob
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('wordprocessingml')) {
        const responseText = await response.text();
        console.error('❌ Réponse inattendue du serveur:', responseText);
        throw new Error('Le serveur n\'a pas retourné un fichier Word valide');
      }

      const blob = await response.blob();
      console.log('✅ Fichier Word généré:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        throw new Error('Le fichier généré est vide');
      }
      
      return blob;
    } catch (error) {
      console.error('❌ Erreur lors de la génération du fichier Word:', error);
      // Si c'est déjà une Error, la relancer telle quelle
      if (error instanceof Error) {
        throw error;
      }
      // Sinon, créer une nouvelle Error
      throw new Error(error instanceof Error ? error.message : 'Erreur inconnue lors de la génération du fichier Word');
    }
  }
}

// Instance globale du service
export const contentGenerationService = new ContentGenerationService();
